import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { auth } from "../../../utils/auth";
import { formatCurrency, formatNumber } from "../../../utils/helpers";
import { request } from "../../../utils/request";
import Loader from "../../general/Loader";

const TOTALES_VACIOS = {
	costo: 0,
	costo_real: 0,
	monto: 0,
	notas_credito: 0,
	beneficio: 0,
	porcentaje: 0,
};

const ReporteBalanceDetalle = () => {
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);

	const [facturas, setFacturas] = useState([]);
	const [totales, setTotales] = useState(TOTALES_VACIOS);
	const [conteo, setConteo] = useState(0);
	const [limiteAviso, setLimiteAviso] = useState(5000);
	const [listo, setListo] = useState(false);
	const [error, setError] = useState(null);
	// El aviso de volumen bloquea la impresión automática hasta que el usuario
	// decida; imprimir decenas de miles de filas rara vez es lo que quiere.
	const [imprimirIgual, setImprimirIgual] = useState(false);

	useEffect(() => {
		let activo = true;

		request("reportes/balance-detalle" + window.location.search).then((res) => {
			if (!activo) return;

			// request() no verifica response.ok y devuelve el Error del catch,
			// así que un 500 llega aquí sin `success`.
			if (!res || !res.success || !res.data) {
				setError(
					typeof res?.data === "string"
						? res.data
						: "No se pudo generar el reporte. Intente de nuevo o acote el rango de fechas."
				);
				setListo(true);
				return;
			}

			setFacturas(res.data.facturas || []);
			setTotales(res.data.totales || TOTALES_VACIOS);
			setConteo(res.data.conteo || 0);
			setLimiteAviso(res.data.limite_aviso || 5000);
			setListo(true);
		});

		return () => {
			activo = false;
		};
	}, []);

	const excedeLimite = conteo > limiteAviso;

	// Espera a que los datos estén en el DOM antes de abrir el diálogo de
	// impresión, en vez de correr contra un setTimeout.
	useEffect(() => {
		if (listo && !error && (!excedeLimite || imprimirIgual)) {
			window.print();
		}
	}, [listo, error, excedeLimite, imprimirIgual]);

	const user = auth.getUser();
	const sucursal = user.sucursales.filter(
		(suc) => suc.id === user.sucursalActual
	)[0];

	if (!listo) {
		return <Loader />;
	}

	if (error) {
		return (
			<div className="text-center p-t-40">
				<h4>{error}</h4>
			</div>
		);
	}

	return (
		<>
			<style>{`
                @media print {
                    @page {
                        margin: 0.5in 0.25in;
                        @bottom-right {
                            content: "Página " counter(page);
                            font-size: 12px;
                        }
                    }
                    .no-print {
                        display: none !important;
                    }
                }
            `}</style>

			{excedeLimite && !imprimirIgual && (
				<div className="alert alert-warning no-print text-center">
					Este reporte trae {formatNumber(conteo)} facturas. Acote el
					rango de fechas o agregue filtros para una impresión más
					manejable.{" "}
					<button
						className="btn btn-sm btn-primary m-l-10"
						onClick={() => setImprimirIgual(true)}
					>
						Imprimir de todos modos
					</button>
				</div>
			)}

			<div className="row">
				<h2 className="col-md-12 text-center m-b-5">
					Balance Facturas en Detalle
				</h2>
				<div className="col-md-12 text-center">
					{sucursal.nombre} - {sucursal.direccion} •{" "}
					{queryParams.get("desde")
						? `${dayjs(queryParams.get("desde")).format(
								"DD/MM/YYYY"
						  )} - `
						: ""}{" "}
					{dayjs(queryParams.get("hasta")).format("DD/MM/YYYY")}
					<div>
						<small>Este reporte no contempla el ITBIS.</small>
					</div>
				</div>
				<div className="col-md-12 text-right p-b-10">
					<div className="float-left">TOTAL FACTURAS: {conteo}</div>
				</div>
			</div>
			<table className="table m-b-40">
				<thead>
					<tr>
						<th>NCF/Numero</th>
						<th>Cliente</th>
						<th width="100px">Fecha</th>
						<th className="text-right">Estado</th>
						<th className="text-right">Costo</th>
						<th className="text-right">Importe</th>
						<th className="text-right">NC</th>
						<th className="text-right">Utilidad Bruta</th>
						<th className="text-right">% Utilidad</th>
					</tr>
				</thead>
				<tbody>
					{facturas.map((factura) => (
						<tr key={factura.id}>
							<td>{factura.numero_comprobante}</td>
							<td>{factura.cliente}</td>
							<td>{factura.fecha}</td>
							<td className="text-capitalize text-right">
								{factura.estado}
							</td>
							<td className="text-right">
								{formatCurrency(factura.costo_real)}
							</td>
							<td className="text-right">
								{formatCurrency(factura.monto)}
							</td>
							<td className="text-right">
								{formatCurrency(factura.notas_credito)}
							</td>
							<td className="text-capitalize text-right">
								{formatCurrency(factura.beneficio)}
							</td>
							<td className="text-right">
								{formatNumber(factura.porcentaje || 0)}%
							</td>
						</tr>
					))}
					<tr>
						<th></th>
						<th></th>
						<th></th>
						<th className="text-right"></th>
						<th className="text-right">
							{formatCurrency(totales.costo_real)}
						</th>
						<th className="text-right">
							{formatCurrency(totales.monto)}
						</th>
						<th className="text-right">
							{formatCurrency(totales.notas_credito)}
						</th>
						<th className="text-right">
							{formatCurrency(totales.beneficio)}
						</th>
						<th className="text-right">
							{formatNumber(totales.porcentaje)}%
						</th>
					</tr>
				</tbody>
			</table>
			<div className="row">
				<div className="col-md-12 text-right p-b-10">
					<div className="float-left">TOTAL FACTURAS: {conteo}</div>
				</div>
			</div>
		</>
	);
};

export default ReporteBalanceDetalle;
